/**
 * XSTV - Application Constants
 * Ported from the original vanilla JS constants
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://1zidol.net/api/index.php',
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  RETRY_ATTEMPTS: 3,
  DEBUG: import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true' || import.meta.env.DEV
};

// API Actions - Only include those that are actually used
export const API_ACTIONS = {
  // Authentication
  LOGIN: 'login',
  REGISTER: 'register',
  LOGOUT: 'logout',
  REFRESH_TOKEN: 'refreshToken',
  CHECK_USER: 'checkUser',
  CHANGE_PASSWORD: 'changePassword',

  // Telegram actions
  CONNECT_TELEGRAM: 'connectTelegram',
  REMOVE_TELEGRAM: 'removeTelegram',
  USER_GET_TELEGRAM: 'userGetTelegram',
  ADMIN_TELEGRAM_CONNECT: 'adminTelegramConnect',
  ADMIN_TELEGRAM_STATUS: 'adminTelegramStatus',
  ADMIN_TELEGRAM_CLEAR: 'adminClearTelegramBot',
  ADMIN_TELEGRAM_DISCONNECT: 'adminTelegramDisconnect',

  // Profile actions (used by all roles)
  USER_PROFILE: 'userProfile',
  USER_UPDATE_PROFILE: 'userUpdateProfile',
  AGENT_PROFILE: 'agentProfile',
  AGENT_UPDATE_PROFILE: 'agentUpdateProfile',
  ADMIN_PROFILE: 'adminProfile',
  ADMIN_UPDATE_PROFILE: 'adminUpdateProfile',

  // Data Management actions
  ADMIN_DATA_EXPORT: 'adminDataExport',
  ADMIN_DATA_IMPORT: 'adminDataImport',
  ADMIN_DATA_LIST: 'adminDataList',
  ADMIN_DATA_DELETE: 'adminDataDelete',
  ADMIN_DATA_CLEAR: 'adminDataClear',

  // Account Management actions
  GET_ACCOUNTS_STATS: 'adminGetAccountsStats',

  // User Management actions
  GET_USERS: 'adminGetUsers',
  CREATE_USER: 'adminCreateUser',
  UPDATE_USER: 'adminUpdateUser',
  DELETE_USER: 'adminDeleteUser',
  TOGGLE_USER_STATUS: 'adminToggleUserStatus',

  // Agent Management actions
  GET_AGENTS: 'adminGetAgents',
  CREATE_AGENT: 'adminCreateAgent',
  UPDATE_AGENT: 'adminUpdateAgent',
  DELETE_AGENT: 'adminDeleteAgent',
  TOGGLE_AGENT_STATUS: 'adminToggleAgentStatus',
  UPDATE_AGENT_BALANCE: 'adminUpdateAgentBalance',

  // Admin Management actions
  GET_ADMINS: 'adminGetAdmins',
  CREATE_ADMIN: 'adminCreateAdmin',
  UPDATE_ADMIN: 'adminUpdateAdmin',
  DELETE_ADMIN: 'adminDeleteAdmin',
  TOGGLE_ADMIN_STATUS: 'adminToggleAdminStatus',
  UPDATE_ADMIN_ROLE: 'updateAdminRole',

  // Lottery Management actions
  GET_LOTTERY_TYPES: 'adminGetLotteryTypes',
  CREATE_LOTTERY_TYPE: 'adminCreateLotteryType',
  UPDATE_LOTTERY_TYPE: 'adminUpdateLotteryType',
  DELETE_LOTTERY_TYPE: 'adminDeleteLotteryType',
  TOGGLE_LOTTERY_TYPE_STATUS: 'adminToggleLotteryTypeStatus',

  // Bet Type Management actions
  GET_BET_TYPES: 'adminGetBetTypes',
  CREATE_BET_TYPE: 'adminCreateBetType',
  UPDATE_BET_TYPE: 'adminUpdateBetType',
  DELETE_BET_TYPE: 'adminDeleteBetType',
  TOGGLE_BET_TYPE_STATUS: 'adminToggleBetTypeStatus',

  // Draw Result Management actions
  GET_DRAW_RESULTS: 'adminGetDrawResults',
  CREATE_DRAW_RESULT: 'adminCreateDrawResult',
  UPDATE_DRAW_RESULT: 'adminUpdateDrawResult',
  DELETE_DRAW_RESULT: 'adminDeleteDrawResult',
  CONFIRM_DRAW_RESULT: 'adminConfirmDrawResult',
  GET_DRAW_RESULT_STATS: 'adminGetDrawResultStats',
  GET_XSMB_RESULT: 'adminGetXSMBResult',

  // Commission Management actions
  GET_COMMISSION_TIERS: 'adminGetCommissionTiers',
  CREATE_COMMISSION_TIER: 'adminCreateCommissionTier',
  UPDATE_COMMISSION_TIER: 'adminUpdateCommissionTier',
  DELETE_COMMISSION_TIER: 'adminDeleteCommissionTier',
    GET_COMMISSION_HISTORY: 'adminGetCommissionHistory',
  CALCULATE_WEEKLY_COMMISSIONS: 'adminCalculateWeeklyCommissions',
  MANUAL_COMMISSION_PAYMENT: 'adminManualCommissionPayment',

  // Bet Management actions
  GET_BET_HISTORY: 'adminGetBetHistory',
  GET_BET_DETAILS: 'adminGetBetDetails',
  GET_AGENT_BOOKINGS: 'adminGetAgentBookings',
  REJECT_PARTIAL_BET: 'adminRejectPartialBet',
  GET_BET_STATS: 'adminGetBetStats',
  GET_PENDING_BETS: 'adminGetPendingBets',

  // User Statistics Tool actions
  USER_GET_TODAY_RESULTS: 'userGetTodayResults',
  USER_GET_AI_ANALYSIS: 'userGetAIAnalysis',
  USER_GET_NUMBER_PATTERNS: 'userGetNumberPatterns',
  USER_GET_LOTTERY_HISTORY: 'userGetLotteryHistory',
  USER_GET_PREDICTIONS: 'userGetPredictions',
  USER_GET_ANALYSIS_STATS: 'userGetAnalysisStats',

  // Financial Management actions
  GET_TRANSACTIONS: 'getTransactions',
  APPROVE_DEPOSIT: 'approveDeposit',
  REJECT_DEPOSIT: 'rejectDeposit',
  APPROVE_WITHDRAWAL: 'approveWithdrawal',
  REJECT_WITHDRAWAL: 'rejectWithdrawal',
  CREATE_TRANSACTION: 'createTransaction',
  GET_TRANSACTION_STATS: 'getTransactionStats'
} as const;

// User Roles - Used in auth store and routing
export const USER_ROLES = {
  ADMIN: 'admin',
  AGENT: 'agent',
  USER: 'user'
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// HTTP Status Codes - Used in error handling
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  TOKEN_EXPIRED: 419,
  RATE_LIMIT: 429
} as const;

// Security Configuration - Used in hooks and middleware
export const SECURITY_CONFIG = {
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes
  SESSION_REFRESH_INTERVAL: 30 * 1000,    // 30 seconds
  MAX_LOGIN_ATTEMPTS: 5,
  LOGIN_LOCKOUT_DURATION: 15 * 60 * 1000  // 15 minutes
} as const;

// Error Messages - Used in error handling
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Lỗi kết nối mạng',
  UNAUTHORIZED: 'Phiên đăng nhập hết hạn',
  FORBIDDEN: 'Không có quyền truy cập',
  NOT_FOUND: 'Không tìm thấy dữ liệu',
  SERVER_ERROR: 'Lỗi máy chủ',
  VALIDATION_ERROR: 'Dữ liệu không hợp lệ',
  INSUFFICIENT_BALANCE: 'Số dư không đủ',
  INVALID_CREDENTIALS: 'Sai tên đăng nhập hoặc mật khẩu'
} as const;